import { useEffect, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { Button } from "@NBCUDTC/reactjs-utility-belt/components/atoms/Button";
import { Icon } from "@NBCUDTC/reactjs-utility-belt/components/atoms/Icon";

import {
  fetchBadgeString,
  fetchGenreString,
  getProviderLogo,
} from "@/utils/TileUtils";
import { platformAtom } from "@/constants/atoms";
import { Platform } from "@/types";
import { useClassNames } from "@/utils/classNames";
import { Image } from "@/components/atoms/Image";
import { LockedSash } from "@/components/atoms/LockedSash";
import { LockedSashSize } from "@/components/atoms/LockedSash/types";
import {
  TileProgressBarSize,
  TileProgressBarStyle,
} from "@/components/atoms/TileProgressBar/types";
import { CoreBadge } from "@/components/atoms/CoreBadge";
import {
  CoreBadgeSize,
  CoreBadgeVariant,
} from "@/components/atoms/CoreBadge/types";
import { isSeries } from "@/utils/FetchAssetDetails/types";
import { TileProgressBar } from "@/components/atoms/TileProgressBar";
import { fetchTuneInBadgeDetails } from "@/utils/FetchTuneInBadgeDetails";
import { ButtonVariant } from "@/components/atoms/Button/types";
import { Text } from "@/components/atoms/Text";
import { ImageType, PeacockNodeType } from "@/utils/shared-atom-types";
import { getImageUrl } from "@/utils/GetImageUrl";
import { TileSkeleton } from "@/components/atoms/TileSkeleton";
import { StreamingIcons } from "@/assets/iconFont/streaming-icons";
import { AssetWithImageType } from "@/utils/FetchAssetDetails/types";
import { TileFormat } from "@/utils/GetTileFormat/types";
import coreBadgeStyles from "@/components/atoms/CoreBadge/CoreBadge.module.scss";

import styles from "./Tile34.module.scss";
import { Tile34Props } from "./types";
import { ButtonGroup } from "../ButtonGroup";
import { ButtonGroupOrientation } from "../ButtonGroup/types";

/**
 * Tile34 component represents a 3:4 content tile that displays an asset with
 * optional badges, lock indicators, and progress bar.
 *
 * @param asset - AssetResponse object containing metadata for this tile
 * @param format - Defines the tile's state (VOD, Continue Watching, Upcoming, Live)
 * @param locked - Whether this tile should show the locked sash
 * @param progress - Number defining the percentage of the progress bar shown for Continue Watching and Live content
 * @param loading - When true, shows skeleton placeholder instead of content
 * @param className - Additional class names for styling
 *
 * @example
 * ```tsx
 * <Tile34
 *   asset={assetResponse}
 *   format={TileFormat.VOD}
 *   locked={false}
 *   progress={30}
 * />
 *
 * // Loading state
 * <Tile34 loading />
 * ```
 */
const Tile34 = ({
  asset,
  format = TileFormat.VOD,
  locked = false,
  progress = 0,
  contentTag,
  loading = false,
  className,
  onTileClick,
}: Tile34Props) => {
  const platform = useAtomValue(platformAtom);
  const [isFocused, setIsFocused] = useState(false);
  const [isExtendedFocus, setIsExtendedFocus] = useState(false);
  const [tuneInBadgeText, setTuneInBadgeText] = useState<
    string | string[] | null
  >(null);
  const focusTimerRef = useRef<NodeJS.Timeout | null>(null);
  const buttonGroupRef = useRef<HTMLDivElement | null>(null);
  const isMobile = platform === Platform.Mobile;
  const isWeb = platform === Platform.Web;
  const isTV = platform === Platform.TV;
  const isCollectionOrHub =
    format === TileFormat.Collection || format === TileFormat.Hub;
  /** Determine if the format is one of Collection, Hub, Clip, or Playlist (provider logo and badge info are not fetched in this case) */
  const isCollectionHubClipPlaylist =
    format === TileFormat.Clip ||
    format === TileFormat.Playlist ||
    format === TileFormat.Collection ||
    format === TileFormat.Hub;

  const shouldShowExtendedFocus =
    format !== TileFormat.ContinueWatching && !isMobile && !isWeb;

  const handleFocus = () => {
    setIsFocused(true);
    focusTimerRef.current = setTimeout(() => {
      setIsExtendedFocus(true);
    }, 1000);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsFocused(false);
    setIsExtendedFocus(false);
    if (focusTimerRef.current) {
      clearTimeout(focusTimerRef.current);
      focusTimerRef.current = null;
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onTileClick && asset) {
      e.preventDefault();
      onTileClick(asset);
    }
  };

  useEffect(() => {
    /** Skip all effects when in loading state */
    if (loading || !asset) return;

    const fetchBadgeText = async () => {
      /** Skip tune-in badge fetching for Collection, Hub, Clip, Playlist, and Link tiles */
      /** These tile types don't have the badging.tuneInBadging structure */
      if (
        isCollectionHubClipPlaylist ||
        asset.type === PeacockNodeType.Link ||
        asset.type === PeacockNodeType.Collection
      ) {
        setTuneInBadgeText(null);
        return;
      }

      try {
        const result = await fetchTuneInBadgeDetails(asset, true);
        setTuneInBadgeText(result);
      } catch (error) {
        /** Silently fail - errors are expected for restricted content */
        setTuneInBadgeText(null);
      }
    };

    fetchBadgeText();
  }, [loading, asset, format, isCollectionHubClipPlaylist]);

  useEffect(() => {
    if (loading) return;
    if (isExtendedFocus && buttonGroupRef.current) {
      const button = buttonGroupRef.current.querySelector(
        `.${styles["focused-button"]}`,
      ) as HTMLButtonElement | null;
      button?.focus();
    }
  }, [loading, isExtendedFocus]);

  useEffect(
    () => () => {
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current);
      }
    },
    [],
  );

  const loadingSkeletonCn = useClassNames(styles, styles["tile-34"], className);

  const borderWrapperCn = useClassNames(
    styles,
    styles["border-wrapper"],
    isExtendedFocus && shouldShowExtendedFocus && styles["extended-focus"],
  );

  const formatClass = styles[`format-${format}`];

  const cn = useClassNames(
    styles,
    styles["tile-34"],
    formatClass,
    locked && styles["locked"],
    isFocused && !isMobile && !isWeb && styles["focus"],
    isExtendedFocus && shouldShowExtendedFocus && styles["extended-focus"],
    tuneInBadgeText && format === TileFormat.VOD && styles["tune-in-tile"],
    className,
  );

  const tuneInBadgeCn = useClassNames(
    styles,
    styles["tune-in-badge"],
    isTV && coreBadgeStyles["dark"],
  );

  if (loading) {
    return <TileSkeleton className={loadingSkeletonCn} />;
  }

  /** If not loading, asset is required */
  if (!asset) {
    return null;
  }

  /** Select image type based on format or asset's imageType property */
  /** If asset has imageType set (e.g., "connectfocus" for Brand rails), use that
  Otherwise, Hub and Collection tiles use scene34, everything else uses titleArt34 */
  const assetWithImageType = asset as AssetWithImageType;
  const imageType: ImageType =
    assetWithImageType.imageType ||
    (format === TileFormat.Hub || format === TileFormat.Collection
      ? ImageType.Scene34
      : ImageType.TitleArt34);
  const imgUrl = getImageUrl(asset, imageType);

  /** Fallback logo and title text for collection/hub tiles */
  const fallBackLogoUrl = isCollectionOrHub
    ? getImageUrl(asset, ImageType.TitleLogo)
    : null;

  const fallbackTitleText =
    isCollectionOrHub && !fallBackLogoUrl ? asset.attributes.title : null;

  const providerLogo =
    !isWeb && !isCollectionHubClipPlaylist ? getProviderLogo(asset) : null;
  const badgeText = fetchBadgeString(asset);
  const labelText = fetchGenreString(asset, { indices: [1] });
  const assetTitle = asset.attributes.title;

  const renderExtendedFocusedContent = () => {
    const getFocusedButtonConfig = () => {
      if (format === TileFormat.Upcoming) {
        return { icon: StreamingIcons.Add, text: "My Stuff" };
      }
      if (isCollectionOrHub) {
        return { icon: StreamingIcons.HubsFilled, text: "Explore" };
      }
      return { icon: StreamingIcons.Play, text: "Watch" };
    };
    const getSecondaryButtonConfig = () => {
      if (isSeries(asset)) {
        return { icon: StreamingIcons.InfoFilled, text: "Episodes" };
      }
      return { icon: StreamingIcons.InfoFilled, text: "Info" };
    };

    const focusedButton = getFocusedButtonConfig();
    const secondaryButton = getSecondaryButtonConfig();
    const showSecondaryButton = !isCollectionHubClipPlaylist;

    return (
      <div ref={buttonGroupRef}>
        <ButtonGroup
          orientation={ButtonGroupOrientation.Vertical}
          className={styles["button-group"]}
        >
          {showSecondaryButton && (
            <Button
              variant={ButtonVariant.Primary}
              className={
                showSecondaryButton
                  ? styles["secondary-button"]
                  : styles["focused-button"]
              }
            >
              <Icon icon={secondaryButton.icon} className={styles["info"]} />
              {secondaryButton.text}
            </Button>
          )}

          <Button
            variant={ButtonVariant.Primary}
            className={styles["focused-button"]}
          >
            <Icon icon={focusedButton.icon} className={styles["info"]} />
            {focusedButton.text}
          </Button>
        </ButtonGroup>
      </div>
    );
  };

  const renderBelowContent = () => (
    <>
      {format === TileFormat.VOD && tuneInBadgeText && (
        <CoreBadge
          className={tuneInBadgeCn}
          variant={CoreBadgeVariant.TuneInBanner34}
        >
          {tuneInBadgeText}
        </CoreBadge>
      )}
      {(format === TileFormat.ContinueWatching ||
        format === TileFormat.Live ||
        format === TileFormat.Upcoming ||
        format === TileFormat.Clip ||
        format === TileFormat.Playlist ||
        (isMobile && isCollectionHubClipPlaylist)) && (
        <div className={styles["title"]}>{assetTitle}</div>
      )}
    </>
  );

  const renderOverlayContent = () => {
    const badgeSize =
      isMobile || isWeb ? CoreBadgeSize.Size16 : CoreBadgeSize.Size24;
    const showProgressBar =
      format === TileFormat.ContinueWatching || format === TileFormat.Live;

    const badgeConfig =
      format === TileFormat.Live
        ? { variant: CoreBadgeVariant.Live, text: "Live" }
        : format === TileFormat.Upcoming && badgeText
          ? { variant: CoreBadgeVariant.White, text: badgeText }
          : null;

    return (
      <>
        {locked && (
          <LockedSash
            className={styles["locked-sash"]}
            size={LockedSashSize.Small}
          />
        )}
        {providerLogo && !isMobile && !isWeb && (
          <div className={styles["gradient"]}>
            <Image className={styles["logo"]} src={providerLogo} />
          </div>
        )}
        {badgeConfig && (
          <CoreBadge
            className={styles["badge"]}
            variant={badgeConfig.variant}
            size={badgeSize}
          >
            {badgeConfig.text}
          </CoreBadge>
        )}
        {showProgressBar && (
          <TileProgressBar
            className={styles["progress-bar"]}
            size={TileProgressBarSize.Medium}
            barStyle={TileProgressBarStyle.BottomFixed}
            progress={progress}
          />
        )}
      </>
    );
  };

  const renderCollectionContent = () => {
    const showLabel =
      format === TileFormat.Hub ||
      format === TileFormat.Playlist ||
      format === TileFormat.Collection ||
      (format === TileFormat.Clip && platform !== Platform.TV);
    const labelConfig = contentTag
      ? contentTag
      : format === TileFormat.Clip || (isWeb && format === TileFormat.Live)
        ? labelText
        : format;

    return (
      <>
        {(showLabel || (isWeb && format === TileFormat.Live)) && (
          <>
            {(format === TileFormat.Clip || format === TileFormat.Live) &&
              isWeb && <div className={styles["genre-gradient"]} />}
            <Text
              variant="label"
              className={`${styles["content-tag"]} ${format === TileFormat.Clip || format === TileFormat.Live ? styles["genre"] : ""}`}
            >
              {labelConfig}
            </Text>
          </>
        )}
        <Image
          className={styles["collection-logo"]}
          src={fallBackLogoUrl ?? undefined}
        />
        {fallbackTitleText && !isMobile && (
          <Text variant="label" className={styles["collection-title-text"]}>
            {fallbackTitleText}
          </Text>
        )}
      </>
    );
  };

  return (
    <div
      className={cn}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={handleClick}
      tabIndex={0}
    >
      {isTV ? (
        <div className={borderWrapperCn}>
          <div className={styles["img-wrapper"]}>
            <Image className={styles["img"]} src={imgUrl} alt={assetTitle} />
            {renderCollectionContent()}
            {!isFocused && renderOverlayContent()}
            {isExtendedFocus &&
              shouldShowExtendedFocus &&
              renderExtendedFocusedContent()}
          </div>
        </div>
      ) : (
        <div className={styles["img-wrapper"]}>
          {format === TileFormat.ContinueWatching && (
            <button
              className={styles["overflow-action"]}
              aria-label="More options"
              onClick={(e) => {
                e.stopPropagation();
                /** Add your overflow action handler here */
              }}
            >
              <span className={styles["overflow-action-dot"]}></span>
              <span className={styles["overflow-action-dot"]}></span>
              <span className={styles["overflow-action-dot"]}></span>
            </button>
          )}
          <Image className={styles["img"]} src={imgUrl} alt={assetTitle} />
          {renderCollectionContent()}
          {renderOverlayContent()}
        </div>
      )}
      {renderBelowContent()}
    </div>
  );
};

export { Tile34 };
